/**
 * Shared utility functions for pre-order components
 */

import * as z from "zod/v4";
import type { CustomerPreOrder, PreOrderItem } from "../../../common/jsonRpcTypes";
import { withDietaryNamesFromForNames } from "../../../common/dietaryRequirements";

export interface MenuLimits {
  [menuId: string]: {
    max?: number;
    min?: number;
  };
}

export function getPersonItemRows(
  items: CustomerPreOrder["items"],
  itemId: string,
  menuId: string,
  person: number | undefined,
): Pick<PreOrderItem, "quantity" | "options">[] {
  const rows: Pick<PreOrderItem, "quantity" | "options">[] = [];

  for (const item of items) {
    if (
      item.id === itemId &&
      item.menu_id === menuId &&
      item.quantity > 0 &&
      (person === undefined || (item.for_name_index ?? 0) === person)
    ) {
      rows.push({ quantity: item.quantity, options: item.options });
    }

    if (item.type !== "menu" || !Array.isArray(item.items)) continue;

    for (const nestedItem of item.items) {
      const nestedPerson = nestedItem.for_name_index ?? item.for_name_index ?? 0;
      const nestedMenuMatches = item.menu_id === menuId || nestedItem.menu_id === menuId;
      if (
        nestedItem.id === itemId &&
        nestedMenuMatches &&
        nestedItem.quantity > 0 &&
        (person === undefined || nestedPerson === person)
      ) {
        rows.push({ quantity: nestedItem.quantity, options: nestedItem.options });
      }
    }
  }

  return rows;
}

export function getPersonItemQuantity(
  items: CustomerPreOrder["items"],
  itemId: string,
  menuId: string,
  person: number | undefined,
): number {
  return getPersonItemRows(items, itemId, menuId, person).reduce((total, row) => total + row.quantity, 0);
}

export function resolveTypedQuantity(value: number | null, multiplier: number): number {
  if (value === null || Number.isNaN(value) || value <= 0) return 0;
  return Math.max(multiplier, Math.round(value / multiplier) * multiplier);
}

/**
 * Identifies one guest's line of one item within one menu. The menu view and
 * the basket build the same key, so an in-flight update locks both rows.
 */
export function preorderItemKey(itemId: string, menuId: string, personIndex: number): string {
  return `${itemId}_${menuId}_${personIndex}`;
}

/**
 * Collins reports an item's energy content in kilocalories on only some items,
 * and it arrives through `/getProcessedMenuData`, whose `processedItems` the
 * RPC contract does not type. Validating rather than casting keeps that
 * unchecked boundary honest — the same API is already known to send `null` and
 * non-numeric values in numeric fields (see `toAmount` in transformPreOrderItem).
 */
const itemCaloriesParser = z.object({ calories: z.number().positive() });

/**
 * Reads an item's calorie count, when the venue has configured one.
 *
 * Zero and negative values are treated as unset: a venue that leaves the field
 * blank should not advertise "(0kcal)".
 *
 * @param item - A menu item from either the processed menu data or the basket
 * @returns The calorie count, or undefined when the item carries none
 */
export function getItemCalories(item: unknown): number | undefined {
  const parsed = itemCaloriesParser.safeParse(item);
  return parsed.success ? parsed.data.calories : undefined;
}

export interface DietaryRequirement {
  name?: string;
  diet_types?: string[];
  allergies?: string[];
  notes?: string;
}

/** A dietary requirement in the shape the `/updateDietaryRequirements` route expects. */
export interface DietaryRequirementPayload {
  name: string;
  diet_types: string[];
  allergies: string[];
  notes: string;
}

/**
 * Normalises dietary requirements into the fully-populated shape the
 * `/updateDietaryRequirements` route requires, where every field is mandatory.
 * @param requirements - Dietary requirements with optional fields
 * @returns The same requirements with defaults applied for any missing field
 */
export function toDietaryRequirementsPayload(requirements: readonly DietaryRequirement[]): DietaryRequirementPayload[] {
  return requirements.map((req) => ({
    name: String(req.name ?? ""),
    diet_types: Array.isArray(req.diet_types) ? req.diet_types : [],
    allergies: Array.isArray(req.allergies) ? req.allergies : [],
    notes: String(req.notes ?? ""),
  }));
}

/**
 * Removes the dietary requirements at a person's index, keeping the remaining
 * entries in the positional order that pairs them with `forNames`.
 * @param requirements - Dietary requirements, one entry per person
 * @param index - Index of the person to drop
 * @returns The remaining requirements, normalised for the update route
 */
export function pruneDietaryRequirementAt(
  requirements: readonly DietaryRequirement[],
  index: number,
): DietaryRequirementPayload[] {
  return toDietaryRequirementsPayload(requirements.filter((_, reqIndex) => reqIndex !== index));
}

/**
 * Names each dietary requirement after the guest at the same position, so a
 * name added or edited after the requirements were saved reaches Collins.
 * @param requirements - Dietary requirements, one entry per person
 * @param forNames - The guests being ordered for
 * @returns The requirements with their names aligned, normalised for the update route
 */
export function alignDietaryRequirementNames(
  requirements: readonly DietaryRequirement[],
  forNames: readonly string[],
): DietaryRequirementPayload[] {
  return toDietaryRequirementsPayload(withDietaryNamesFromForNames(requirements, forNames));
}

/**
 * Whether any dietary requirement is missing, or carries a different name from,
 * the guest at its position.
 * @param requirements - Dietary requirements, one entry per person
 * @param forNames - The guests being ordered for
 */
export function hasStaleDietaryRequirementNames(
  requirements: readonly DietaryRequirement[] | undefined,
  forNames: readonly string[],
): boolean {
  return (requirements ?? []).some((req, index) => index < forNames.length && req.name !== forNames[index]);
}

/**
 * Derives the single order-level note held in the dietary requirements.
 *
 * The dietary modal's "Order notes" is one note for the whole pre-order, stored
 * mirrored on every dietary requirement entry so the existing Redis/Collins
 * persistence keeps it. The first non-empty note is the order note; earlier
 * sessions may hold divergent per-person values, which the next modal save
 * re-mirrors.
 *
 * @param requirements - Dietary requirements carrying the order note
 * @returns The order note, or undefined when none was entered
 */
export function dietaryRequirementsToCustomerNotes(
  requirements: readonly DietaryRequirement[] | undefined,
): string | undefined {
  return (requirements ?? []).map((req) => req.notes?.trim() ?? "").find((n) => n.length > 0);
}

/**
 * Returns the dietary requirements with every entry carrying the order note.
 *
 * Used to write the confirm-stage "Order notes" back into the pre-order's
 * dietary requirements so reopening the dietary modal shows the same value.
 * An empty requirements list gains a single unnamed entry — the shape a
 * one-person order already produces — so a note added without ever opening the
 * modal is still shown there. With no note either, it stays empty.
 *
 * @param requirements - Current dietary requirements, if any
 * @param note - The order note to apply to every entry
 * @returns The requirements with the shared note applied
 */
export function withSharedOrderNote(
  requirements: readonly DietaryRequirement[] | undefined,
  note: string,
): DietaryRequirementPayload[] {
  if (!requirements?.length) {
    return note ? [{ name: "", diet_types: [], allergies: [], notes: note }] : [];
  }
  return requirements.map((req) => ({
    ...req,
    name: String(req.name ?? ""),
    diet_types: Array.isArray(req.diet_types) ? req.diet_types : [],
    allergies: Array.isArray(req.allergies) ? req.allergies : [],
    notes: note,
  }));
}

/**
 * Menu items reach the frontend through untyped processed-menu and basket
 * payloads, so the allergen list is validated rather than trusted. An absent
 * list means no allergens are listed; any other value that is not a list of
 * strings cannot be read.
 */
const allergenFieldParser = z.object({ allergens: z.array(z.string()).nullish() });

const normaliseAllergen = (allergen: string): string => allergen.trim().toLowerCase();

/**
 * Whether a menu item is free of every allergen the person has listed.
 *
 * Only allergies block an item. Diet types are a preference the menu
 * highlights, never a reason to stop the guest choosing an item. Matching
 * ignores case and surrounding whitespace, and an allergen list that cannot be
 * read counts as unsafe, since a missed allergen is a safety issue.
 *
 * @param item - The menu item, with the allergens it contains
 * @param personIndex - Index of the person in the dietaryRequirements array
 * @param dietaryRequirements - Dietary requirements, one entry per person
 * @returns false when the item contains, or may contain, one of the person's allergens
 */
export function isItemSafeForPersonAllergies(
  item: object,
  personIndex: number,
  dietaryRequirements?: readonly DietaryRequirement[],
): boolean {
  const personAllergies = dietaryRequirements?.[personIndex]?.allergies;
  if (!personAllergies?.length) return true;

  const parsed = allergenFieldParser.safeParse(item);
  if (!parsed.success) return false;

  const blocked = new Set(personAllergies.map(normaliseAllergen));
  return !(parsed.data.allergens ?? []).some((allergen) => blocked.has(normaliseAllergen(allergen)));
}

/** A menu as far as item lookup needs it: its id and its items. */
export interface ItemLookupMenu {
  readonly id: string;
  readonly items: readonly object[];
}

const menuItemIdParser = z.object({ id: z.string() });

/**
 * Finds every entry for an item in the menus Collins sent.
 *
 * Regular basket lines are built without the menu item's allergens, so the
 * menu entries are a source for them alongside the line itself. A menu may be
 * listed more than once (e.g. under a linked booking type), so every matching
 * entry is returned. Headings never match.
 *
 * @param menus - Menus the item may belong to
 * @param menuId - Id of the item's menu
 * @param itemId - Id of the item
 */
export function findMenuItems(menus: readonly ItemLookupMenu[], menuId: string, itemId: string): object[] {
  return menus
    .filter((menu) => menu.id === menuId)
    .flatMap((menu) => menu.items)
    .filter((item) => menuItemIdParser.safeParse(item).data?.id === itemId);
}

/**
 * Converts options array to selectedOptions format
 * @param options - Array of option objects with label and values
 * @returns Object mapping option labels to selected value labels
 */
export function convertOptionsToSelectedOptions(options: any[]): {
  [optionLabel: string]: { [valueId: string]: boolean };
} {
  const selectedOptions: { [optionLabel: string]: { [valueId: string]: boolean } } = {};

  if (!Array.isArray(options)) {
    return selectedOptions;
  }

  for (const option of options) {
    if (!option.label || !Array.isArray(option.values)) {
      continue;
    }

    selectedOptions[option.label] = {};
    for (const value of option.values) {
      if (value.label) {
        // Mark as true since these are the selected values
        selectedOptions[option.label][value.label] = true;
      }
    }
  }

  return selectedOptions;
}

/**
 * Creates a highlight manager hook for managing item highlight animations
 * @returns Object with state, ref, and highlightItem function
 */
export function createHighlightManager() {
  return {
    state: new Set<string>(),
    timeouts: new Map<string, ReturnType<typeof setTimeout>>(),
  };
}

/**
 * Highlights an item with a timeout to remove the highlight
 * @param itemKey - Unique identifier for the item
 * @param highlightedItems - Current set of highlighted items
 * @param setHighlightedItems - State setter for highlighted items
 * @param highlightTimeoutsRef - Ref to map of timeout IDs
 */
export function highlightItem(
  itemKey: string,
  setHighlightedItems: React.Dispatch<React.SetStateAction<Set<string>>>,
  highlightTimeoutsRef: { current: Map<string, ReturnType<typeof setTimeout>> },
): void {
  // Clear any existing timeout for this item
  const existingTimeout = highlightTimeoutsRef.current.get(itemKey);
  if (existingTimeout) {
    clearTimeout(existingTimeout);
  }

  // Add item to highlighted set
  setHighlightedItems((prev) => new Set(prev).add(itemKey));

  // Remove highlight after animation duration (1 second)
  const timeout = setTimeout(() => {
    setHighlightedItems((prev) => {
      const next = new Set(prev);
      next.delete(itemKey);
      return next;
    });
    highlightTimeoutsRef.current.delete(itemKey);
  }, 1000);

  highlightTimeoutsRef.current.set(itemKey, timeout);
}

/**
 * Gets the item multiplier for a menu from customer preorder settings
 * @param customerPreOrder - Customer preorder data
 * @param menuId - ID of the menu to get multiplier for
 * @returns The multiplier value (defaults to 1 if not found)
 */
export function getMenuMultiplier(customerPreOrder: any, menuId: string): number {
  const menu = customerPreOrder.menus?.find((m: any) => m.id === menuId);
  const menuSettings = customerPreOrder.customer_preorder_settings?.menus?.find((m: any) => m.menu_id === menuId);
  return menuSettings?.item_multiplier ?? menu?.item_multiplier ?? 1;
}

/**
 * Determines whether the guest may order for multiple people.
 *
 * Venues can switch off the shareable pre-order link, in which case the
 * multi-person ordering controls must be hidden. The setting reaches the two
 * pre-order flows by different routes: the /book flow reads it from the
 * selected booking type of the create-booking response and passes it in
 * explicitly, whereas /place-your-order reads the top-level flag Collins
 * resolves from the booking type on the customer pre-order payload. Both are
 * optional, and an absent value means ordering for multiple people stays
 * available.
 *
 * @param customerPreOrder - Customer pre-order data, when loaded
 * @param disableShareableLink - Booking-type override supplied by the /book flow
 * @returns true when the multi-person ordering controls should be shown
 */
export function isMultiPersonOrderingEnabled(
  customerPreOrder: CustomerPreOrder | undefined,
  disableShareableLink?: boolean,
): boolean {
  return !(disableShareableLink ?? customerPreOrder?.disable_shareable_link ?? false);
}

/**
 * Whether the party size is a usable cap on the number of pre-order names.
 *
 * The guest count is unknown while the booking is still loading, and a booking
 * type may report no covers at all. Treating those as a cap of zero would lock
 * the guest out of multi-person ordering entirely, so the cap only applies once
 * a positive count is known.
 */
function hasKnownGuestCount(guestCount: number | undefined): guestCount is number {
  return typeof guestCount === "number" && Number.isFinite(guestCount) && guestCount > 0;
}

/**
 * Whether every guest in the booking already has a name, so no further name
 * may be added.
 *
 * @param nameCount - Number of names currently added
 * @param guestCount - Party size of the booking, when known
 */
export function hasReachedNameLimit(nameCount: number, guestCount?: number): boolean {
  return hasKnownGuestCount(guestCount) && nameCount >= guestCount;
}

/**
 * Whether the booking holds more names than guests.
 *
 * This is reachable without the guest adding anything: they can step back
 * through the /book flow and lower the party size after their names were
 * captured. The extra names are kept rather than dropped — silently discarding
 * a person's basket is worse than an over-count — so the guest is told to
 * remove them.
 *
 * @param nameCount - Number of names currently added
 * @param guestCount - Party size of the booking, when known
 */
export function hasMoreNamesThanGuests(nameCount: number, guestCount?: number): boolean {
  return hasKnownGuestCount(guestCount) && nameCount > guestCount;
}

/** Outcome of weighing leftover names against the rest of the basket's validity. */
export interface ExcessNameValidity {
  /** Whether the guest may proceed to the next step. */
  isOrderValid: boolean;
  /** Whether to warn that the booking holds more names than guests. */
  showNamesExceedGuests: boolean;
}

/**
 * Decides whether names left over from a larger party size stop the guest
 * checking out, and whether to say so.
 *
 * Both are tied to the multi-person controls being on screen. A venue can
 * switch off the shareable pre-order link, which hides the names along with
 * the buttons that remove them — blocking checkout then would leave the guest
 * on a disabled button with no way to recover, and warning them would be
 * equally unactionable.
 *
 * @param isBasketValid - The basket's verdict from every other rule
 * @param namesExceedGuests - Whether more names are held than there are guests
 * @param multiPersonOrderingEnabled - Whether the multi-person controls are shown
 */
export function resolveExcessNameValidity(
  isBasketValid: boolean,
  namesExceedGuests: boolean,
  multiPersonOrderingEnabled: boolean,
): ExcessNameValidity {
  const showNamesExceedGuests = multiPersonOrderingEnabled && namesExceedGuests;
  return { isOrderValid: isBasketValid && !showNamesExceedGuests, showNamesExceedGuests };
}

/**
 * Counts the actual number of items in a basket, accounting for nested items in fixed price menus.
 * Fixed price menus store items in a container with type="menu" and nested items array.
 * This function counts the nested items instead of the container's quantity.
 * @param items - Array of basket items
 * @returns The total count of actual items
 */
export function getActualItemCount(items: any[]): number {
  if (!Array.isArray(items)) return 0;

  return items.reduce((sum: number, item: any) => {
    if (item.type === "menu" && Array.isArray(item.items)) {
      // For fixed price menus, count the nested items
      return (
        sum +
        item.items.reduce((nestedSum: number, nestedItem: any) => nestedSum + (Number(nestedItem.quantity) || 1), 0)
      );
    }
    return sum + (Number(item.quantity) || 0);
  }, 0);
}

/**
 * Finds the best-fitting price rule limit for a given item count.
 * Returns the smallest `max_items` value that is >= `itemCount`,
 * preferring limits that are larger than the current match.
 */
function findMatchedPriceRuleLimit(sortedLimits: number[], itemCount: number): number | undefined {
  let matched: number | undefined;
  for (const limit of sortedLimits) {
    if ((matched === undefined || matched < itemCount) && itemCount <= limit) {
      matched = limit;
    }
  }
  return matched;
}

/**
 * Counts the number of fixed-price menu containers whose total nested item
 * quantity does not exactly match the best-fitting price rule's `max_items`.
 *
 * This mirrors the DMN `checkFixedPriceMenuItems` logic: for each basket item
 * of type "menu", find the menu definition, check its `fixed_price_rules`, and
 * determine whether the customer could add more items for the same price.
 *
 * @param items  - The basket items (`CustomerPreOrder["items"]`)
 * @param menus  - The menu definitions (with optional `fixed_price` / `fixed_price_rules`)
 * @returns The number of menu containers where the customer has not filled
 *          up to the matched price rule's `max_items`.
 */
export function getMismatchedPriceRulesCount(
  items: CustomerPreOrder["items"],
  menus: CustomerPreOrder["menus"],
): number {
  if (!Array.isArray(items) || !Array.isArray(menus)) return 0;

  let mismatchCount = 0;

  for (const item of items) {
    if (item.type !== "menu") continue;

    const menuDetails = menus.find((m) => m.id === item.id);
    if (!menuDetails?.fixed_price || !Array.isArray(menuDetails.fixed_price_rules)) continue;

    const priceRuleItemLimits = menuDetails.fixed_price_rules.map((rule) => rule.max_items).sort((a, b) => a - b);

    const numAddedMenuItems = Array.isArray(item.items)
      ? item.items.reduce((sum, nestedItem) => sum + (Number(nestedItem.quantity) || 0), 0)
      : 0;

    const matchedLimit = findMatchedPriceRuleLimit(priceRuleItemLimits, numAddedMenuItems);

    if (matchedLimit !== undefined && matchedLimit !== numAddedMenuItems) {
      mismatchCount++;
    }
  }

  return mismatchCount;
}

/**
 * Returns the total number of additional items the customer can add to a
 * specific fixed-price menu without paying more.  If there are multiple
 * containers (e.g. one per person), the gaps are summed.
 *
 * @param items - The basket items (`CustomerPreOrder["items"]`)
 * @param menu  - The menu definition (with `fixed_price` / `fixed_price_rules`)
 * @returns The number of additional items needed (0 when fully filled or not a fixed-price menu)
 */
export function getAdditionalItemsNeededForMenu(
  items: CustomerPreOrder["items"],
  menu: CustomerPreOrder["menus"][number],
): number {
  if (!menu?.fixed_price || !Array.isArray(menu.fixed_price_rules)) return 0;

  const priceRuleItemLimits = menu.fixed_price_rules.map((rule) => rule.max_items).sort((a, b) => a - b);

  let totalAdditional = 0;

  for (const item of items) {
    if (item.type !== "menu" || item.id !== menu.id) continue;

    const numAddedMenuItems = Array.isArray(item.items)
      ? item.items.reduce((sum, nestedItem) => sum + (Number(nestedItem.quantity) || 0), 0)
      : 0;

    let matchedPriceRuleItemLimit: number | undefined;
    for (const itemLimit of priceRuleItemLimits) {
      if (
        (matchedPriceRuleItemLimit === undefined || matchedPriceRuleItemLimit < numAddedMenuItems) &&
        numAddedMenuItems <= itemLimit
      ) {
        matchedPriceRuleItemLimit = itemLimit;
      }
    }

    if (matchedPriceRuleItemLimit !== undefined && matchedPriceRuleItemLimit !== numAddedMenuItems) {
      totalAdditional += matchedPriceRuleItemLimit - numAddedMenuItems;
    }
  }

  return totalAdditional;
}

/**
 * Returns the number of additional items a customer can add to a single
 * fixed-price menu container (a basket item with `type === "menu"`) without
 * paying more.  Returns 0 when the container is already full or the menu is
 * not a fixed-price menu.
 *
 * @param containerItem - A single basket item with `type === "menu"`
 * @param menus         - The menu definitions (with `fixed_price` / `fixed_price_rules`)
 * @returns The number of additional items that can be added for free
 */
export function getAdditionalItemsForContainer(
  containerItem: CustomerPreOrder["items"][number],
  menus: CustomerPreOrder["menus"],
): number {
  if (containerItem.type !== "menu" || !Array.isArray(menus)) return 0;

  const menuDetails = menus.find((m) => m.id === containerItem.id);
  if (!menuDetails?.fixed_price || !Array.isArray(menuDetails.fixed_price_rules)) return 0;

  const priceRuleItemLimits = menuDetails.fixed_price_rules.map((rule) => rule.max_items).sort((a, b) => a - b);

  const numAddedMenuItems = Array.isArray(containerItem.items)
    ? containerItem.items.reduce((sum, nestedItem) => sum + (Number(nestedItem.quantity) || 0), 0)
    : 0;

  const matchedLimit = findMatchedPriceRuleLimit(priceRuleItemLimits, numAddedMenuItems);

  if (matchedLimit !== undefined && matchedLimit !== numAddedMenuItems) {
    return matchedLimit - numAddedMenuItems;
  }

  return 0;
}

/**
 * Counts items for a specific menu, accounting for nested items in fixed price menus.
 * @param items - Array of basket items
 * @param menuId - The menu ID to filter by
 * @returns The total count of items for the specified menu
 */
export function getActualItemCountForMenu(items: any[], menuId: string): number {
  if (!Array.isArray(items)) return 0;

  return items
    .filter((item: any) => item.menu_id === menuId)
    .reduce((sum: number, item: any) => {
      if (item.type === "menu" && Array.isArray(item.items)) {
        // For fixed price menus, count the nested items
        return (
          sum +
          item.items.reduce((nestedSum: number, nestedItem: any) => nestedSum + (Number(nestedItem.quantity) || 1), 0)
        );
      }
      return sum + (Number(item.quantity) || 0);
    }, 0);
}
